const FileUploadState = new Enumeration(["Initial", "Preparing", "Prepared", "Uploading", "Error", "Successful", "Aborted", "Forbidden"]);

class FileStore {
  constructor() {
    this.databaseName = "FileStore";
    this.fileStoreName = "files";
    this.fileContentsStoreName = "file_contents";
    this.fileThumbnailStoreName = "file_thumbnail";
    this.database = null;

    this.databaseReady = this.openDatabase();
  }

  openDatabase() {
    return new Promise(
      (resolve, reject) => {
        const version = 3;
        const request = indexedDB.open(this.databaseName, version);

        request.onsuccess = (event) => {
          this.database = event.target.result;
          resolve();
        };

        request.onupgradeneeded = (event) => {
          const database = event.target.result;

          if (event.oldVersion < 1) {
            const fileStore = database.createObjectStore(this.fileStoreName, { keyPath: "id", autoIncrement: true });
            fileStore.createIndex("name", "name", { unique: false });
            fileStore.createIndex("state", "state", { unique: false });
            fileStore.createIndex("target", "target", { unique: false });
          }

          if (event.oldVersion < 2)
            database.createObjectStore(this.fileContentsStoreName, { keyPath: "id" });

          if (event.oldVersion < 3) {
            database.createObjectStore(this.fileThumbnailStoreName, { keyPath: "id" });

            const fileStore = event.target.transaction.objectStore(this.fileStoreName);
            fileStore.createIndex("processed", "processed", { unique: false });
            fileStore.createIndex("uuid", "uuid", { unique: false });
          }
        };

        request.onerror = (event) => reject(event.target.error);
      }
    );
  }

  performTransaction(storeNames, mode, callback) {
    return this.databaseReady.then(
      () => new Promise(
        (resolve, reject) => {
          const transaction = this.database.transaction(storeNames, mode);
          const stores = storeNames.map(name => transaction.objectStore(name));
          callback(...stores, transaction, resolve, reject);
        }
      )
    );
  }

  add(file) {
    return this.performTransaction(
      [this.fileStoreName, this.fileContentsStoreName],
      "readwrite",
      (files, contents, transaction, resolve, reject) => {
        const request = files.add({
          target: file.target,
          name: file.name,
          size: file.size,
          state: file.state,
          attempts: file.attempts,
          progress: file.progress,
          problems: file.problems,
          location: file.location,
          processed: 0,
          uuid: null
        });
        request.onsuccess = () => {
          file.id = request.result;
          contents.add({
            id: file.id,
            contents: file.contents
          });
        };

        transaction.oncomplete = () => resolve(file);
        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  addThumbnail(file, contents) {
    return this.performTransaction(
      [this.fileThumbnailStoreName],
      "readwrite",
      (thumbnails, transaction, resolve, reject) => {
        const result = file;
        result.thumbnail = contents;

        const thumbnail = {
          id: file.id,
          contents: contents
        };
        thumbnails.add(thumbnail);

        transaction.oncomplete = () => resolve(result);
        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  delete(key) {
    return this.performTransaction(
      [this.fileStoreName, this.fileContentsStoreName, this.fileThumbnailStoreName],
      "readwrite",
      (files, contents, thumbnails, transaction, resolve, reject) => {
        files.delete(key);
        contents.delete(key);
        thumbnails.delete(key);

        transaction.oncomplete = () => resolve();
        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  deleteContents(file) {
    return this.performTransaction(
      [this.fileStoreName, this.fileContentsStoreName],
      "readwrite",
      (files, contents, transaction, resolve, reject) => {
        file.processed = 1;
        const request = files.put({
          id: file.id,
          target: file.target,
          name: file.name,
          size: file.size,
          state: file.state,
          attempts: file.attempts,
          progress: file.progress,
          problems: file.problems,
          location: file.location,
          processed: file.processed,
          uuid: file.uuid
        });

        request.onsuccess = () => {
          contents.delete(file.id);
        };

        transaction.oncomplete = () => resolve();
        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  deleteMatches(indexName, value) {
    return this.performTransaction(
      [this.fileStoreName, this.fileContentsStoreName, this.fileThumbnailStoreName],
      "readwrite",
      (files, contents, thumbnails, transaction, resolve, reject) => {
        const index = files.index(indexName);
        const range = IDBKeyRange.only(value);
        const request = index.openCursor(range);

        request.onsuccess = () => {
          const cursor = request.result;

          if (cursor) {
            contents.delete(cursor.value.id);
            thumbnails.delete(cursor.value.id);

            cursor.delete();
            cursor.continue();
          }
          else
            transaction.oncomplete = () => resolve();
        };

        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  getThumbnail(key) {
    return this.performTransaction(
      [this.fileStoreName, this.fileThumbnailStoreName],
      "readonly",
      (files, thumbnails, transaction, resolve, reject) => {
        const fileRequest = files.get(key);
        const thumbnailRequest = thumbnails.get(key);

        transaction.oncomplete = () => {
          const file = fileRequest.result;
          const thumbnail = thumbnailRequest.result ? thumbnailRequest.result.contents : null;

          if (file)
            file.thumbnail = thumbnail;

          resolve(file);
        };

        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  getFileContents(key) {
    return this.performTransaction(
      [this.fileContentsStoreName],
      "readonly",
      (contents, transaction, resolve, reject) => {
        let result;

        const request = contents.get(key);
        request.onsuccess = () => {
          result = request.result ? request.result.contents : null;
        };

        transaction.oncomplete = () => resolve(result);
        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  get(key) {
    return this.performTransaction(
      [this.fileStoreName],
      "readonly",
      (files, transaction, resolve, reject) => {
        let result;

        const request = files.get(key);
        request.onsuccess = () => {
          result = request.result;
        };

        transaction.oncomplete = () => resolve(result);
        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  getAll() {
    return this.performTransaction(
      [this.fileStoreName],
      "readonly",
      (files, transaction, resolve, reject) => {
        let result;

        const request = files.getAll();
        request.onsuccess = () => {
          result = request.result;
        };

        transaction.oncomplete = () => resolve(result);
        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  getMatches(indexName, value) {
    return this.performTransaction(
      [this.fileStoreName],
      "readonly",
      (files, transaction, resolve, reject) => {
        const index = files.index(indexName);
        const range = IDBKeyRange.only(value);

        const request = index.getAll(range);
        request.onsuccess = () => {
          resolve(request.result);
        };

        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }

  update(file) {
    return this.performTransaction(
      [this.fileStoreName],
      "readwrite",
      (files, transaction, resolve, reject) => {
        const request = files.put({
          id: file.id,
          target: file.target,
          name: file.name,
          size: file.size,
          state: file.state,
          attempts: file.attempts,
          progress: file.progress,
          problems: file.problems,
          location: file.location,
          processed: file.processed,
          uuid: file.uuid
        });

        transaction.oncomplete = () => resolve();
        transaction.onerror = (event) => reject(event.target.error);
      }
    );
  }
}
